import db from './db.js';
import imageHelper from './imageHelper.js';
import Storage from './storage.js';
import { imageUrl, videoUrl } from './texts.js';
import _ from './translate.js'
import events from './event.js';
import Logger from './logger.js';
import CoverDialog from './coverDialog.js';

class Upload {
    wrapperDiv = null;
    uploadDiv = null;
    dropzoneDiv = null;
    fileInput = null;
    showUploadedImagesButton = null;
    triggerClick = false; // Whether to trigger click on file input automatically

    // Standalone mode used outside the popup, e.g. by the [mscp_shop] upload
    // control. No popup overlay/hold events, no dropzone, and errors go to options.onError
    // (CoverDialog only exists inside the popup).
    standalone = false;
    onError = null;

    constructor(wrapperDiv, triggerClick = false, options = {}) {
        this.wrapperDiv = wrapperDiv;
        this.triggerClick = triggerClick; // Set whether to trigger click on file input automatically
        this.standalone = !!options.standalone;
        this.onError = typeof options.onError === 'function' ? options.onError : null;
        if (!this.wrapperDiv) {
            // Logger.error('Upload failed to initialize.');
            CoverDialog.show({
                type: 'uploadFailed',
                data: { errorMessage: 'Upload failed to initialize.' }
            });
            return;
        }
        this.createUploadButton();

        if (!this.standalone) {
            events.add('reInitUpload', () => {
                events.trigger('changeStep', 'upload');
            });
            // Files dropped on the popup's drag overlay (uploadStep.js).
            events.add('dropFiles', async (files) => {
                if (files?.length) await this.saveImage(files);
            });
        }
    }

    /**
     * Report an upload problem: inline callback in standalone mode, CoverDialog in the popup.
     */
    showError(type, data = {}) {
        if (this.standalone) {
            this.onError?.(type, data);
            return;
        }
        CoverDialog.show({ type, data });
    }

    async init() {
        await this.createDropzone();
    }

    async createDropzone() {
        const activeImage = await imageHelper.getActiveImage();
        Logger.debug('createDropzone called, activeImage:', activeImage);
        if (activeImage) {
            // If an image is already uploaded, remove the dropzone
            Logger.debug('Active image exists, removing dropzone');
            this.dropzoneDiv?.remove();
            return;
        }
        if (this.dropzoneDiv) {
            Logger.debug('Dropzone already exists, skipping creation');
            return; // Dropzone already exists, no need to create again
        }
        Logger.debug('Creating new dropzone');
        this.dropzoneDiv = document.createElement('div');
        this.dropzoneDiv.classList.add('mscp-dropzone');

        // add dropzone text
        const dropzoneText = document.createElement('div');
        dropzoneText.classList.add('mscp-dropzone-text', 'mscp-text-wrapper');
        const dropzoneImageUrl = imageUrl('upload_image');
        if (dropzoneImageUrl) {
            const dropzoneImage = document.createElement('img');
            dropzoneImage.src = dropzoneImageUrl;
            dropzoneText.appendChild(dropzoneImage);

            const dropzoneVideoUrl = videoUrl('upload_video');
            if (dropzoneVideoUrl) {
                const dropzoneVideo = document.createElement('video');
                dropzoneVideo.onloadeddata = () => {
                    dropzoneVideo.classList.add('mscp-video-playing');
                };
                dropzoneVideo.onended = () => {
                    dropzoneVideo.classList.remove('mscp-video-playing');
                };
                dropzoneVideo.autoplay = true;
                dropzoneVideo.loop = true;
                dropzoneVideo.playsInline = true;
                dropzoneVideo.muted = true;
                dropzoneVideo.src = dropzoneVideoUrl;
                dropzoneText.appendChild(dropzoneVideo);
            }
        } else {
            dropzoneText.innerHTML = _("Drag and drop your image here or click to upload");
        }

        // add additional image below upload_info_image
        // const infoImageUrl = imageUrl('upload_info_image');
        // if (infoImageUrl) {
        //     const infoImage = document.createElement('img');
        //     infoImage.src = infoImageUrl;
        //     infoImage.classList.add('mscp-upload-info-image');
        //     dropzoneText?.appendChild(infoImage);
        // }

        this.dropzoneDiv.appendChild(dropzoneText);

        // add drag and drop functionality
        this.dropzoneDiv.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dropzoneDiv.classList.add('dragover');
        });
        this.dropzoneDiv.addEventListener('dragleave', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dropzoneDiv.classList.remove('dragover');
        });
        this.dropzoneDiv.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dropzoneDiv.classList.remove('dragover');
            const files = e.dataTransfer.files;
            await this.saveImage(files);
        });
        this.dropzoneDiv.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dropzoneDiv.classList.remove('dragover');
        });

        this.dropzoneDiv.addEventListener('click', () => {
            this.triggerChange();
        });
        this.wrapperDiv.appendChild(this.dropzoneDiv);
        events.trigger('dropzoneCreated');
        // Logger.debug('Dropzone appended to wrapper, class:', this.dropzoneDiv.className);
        // Logger.debug('Wrapper class:', this.wrapperDiv.className);
        // Logger.debug('Dropzone parent:', this.dropzoneDiv.parentElement);
        // Logger.debug('Dropzone computed display:', window.getComputedStyle(this.dropzoneDiv).display);
        // Logger.debug('Dropzone computed visibility:', window.getComputedStyle(this.dropzoneDiv).visibility);
        // Logger.debug('Dropzone offsetWidth:', this.dropzoneDiv.offsetWidth);
        // Logger.debug('Dropzone offsetHeight:', this.dropzoneDiv.offsetHeight);
        // Logger.debug('Dropzone clientWidth:', this.dropzoneDiv.clientWidth);
        // Logger.debug('Dropzone clientHeight:', this.dropzoneDiv.clientHeight);
        // Logger.debug('Dropzone children:', this.dropzoneDiv.children.length);
        // Logger.debug('Wrapper offsetWidth:', this.wrapperDiv.offsetWidth);
        // Logger.debug('Wrapper offsetHeight:', this.wrapperDiv.offsetHeight);
    }

    createUploadButton() {
        this.fileInput = document.createElement('input');
        this.fileInput.type = 'file';
        this.fileInput.multiple = false;
        this.fileInput.style.display = 'none';

        // do the actual saving when file is selected
        this.fileInput.addEventListener('change', async (e) => {
            if (e.target.files && e.target.files.length > 0) {
                if (await this.saveImage(e.target.files)) {
                    // If the image is saved successfully, remove the dropzone
                    this.dropzoneDiv?.remove();
                }
                this.fileInput.value = "";
            }
        });
        this.wrapperDiv.appendChild(this.fileInput);
        if (this.triggerClick) {
            // Automatically trigger click on file input if triggerClick is true
            this.triggerChange();
        }
    }

    triggerChange() {
        if (this.fileInput) {
            this.fileInput.click();
        } else {
            Logger.error('File input not found. Cannot trigger change.');
        }
    }


    isValidImage(file) {
        if (!file.type.startsWith('image/')) return false;
        if (window.mscp?.validImageTypes && !window.mscp.validImageTypes.includes(file.type)) return false;
        return true;
    }

    async saveImage(files) {
        if (files.length === 0) {
            Logger.warn('No files selected');
            return false;
        }
        // check if file is an image
        if (!this.isValidImage(files[0])) {
            // this.showMessage("Please upload an image file", "error");
            // Logger.error('Selected file is not an image: %s', files[0].type);
            console.error('Selected file is not a valid image type: %s', files[0].type);
            this.showError('invalidFileType', { fileType: files[0].type });
            return false;
        }
        if (window.mscp?.validImageTypes && !window.mscp.validImageTypes.includes(files[0].type)) {
            // this.showMessage("Invalid image type. Please upload a valid image file.", "error");
            // Logger.error('Selected file is not a valid image type: %s', files[0].type);
            console.error('Selected file is not a valid image type: %s', files[0].type);
            this.showError('invalidFileType', { fileType: files[0].type });
            return false;
        }
        // check if file size is too large
        if (files[0].size > window.mscp?.settings?.maxImageSize * 1024 * 1024) {
            // Logger.error('Image is too large. Maximum size is %s MB', window.mscp.settings.maxImageSize);
            console.error('Selected file is too large: %s bytes. Maximum size is %s MB', files[0].size, window.mscp?.settings?.maxImageSize);
            this.showError('fileTooLarge', { maxSize: window.mscp?.settings?.maxImageSize });

            // Logger.error('Selected file is too large:', files[0].size, 'bytes');
            return false;
        }
        try {
            // Lås pop-up:en OCH visa overlay under tiden (popup only)
            if (!this.standalone) {
                events.trigger('showLoadingOverlay', 'upload');
                events.trigger('setPopupHold', 1);
            }

            const image = files[0];
            const result = await db.save(image, { uploadStatus: 'pending' });

            // Om db.save lyckades
            if (result) {
                const orientation = imageHelper.getImageOrientation(result.imageResolution);
                Storage.save('orientation', orientation);

                Logger.log('Image saved in browser.');
                this.dropzoneDiv?.classList.add('mscp-dropzone-success');

                await events.trigger('newImageLocalSaved', {
                    resolution: result.imageResolution,
                    fileName: image.name,
                    status: result.uploadStatus
                });

                return true;
            }

            // Vid fel i db.save
            Logger.debug('Error saving image', result);
            Logger.error('Error saving image');
            this.resetAfterSaveError();

        } catch (error) {
            Logger.debug('Error saving image:', error);
            Logger.error('Error saving image');
            this.resetAfterSaveError();
        }
        return false;
    }


    resetAfterSaveError() {
        if (this.standalone) {
            this.showError('saveFailed');
            return;
        }
        events.trigger('setPopupHold', 0);
        events.trigger('reInitUpload');
    }

    async deleteImage(fileName) {
        if (!fileName) {
            Logger.error('No file name provided for deletion');
            // this.showMessage("No file name provided for deletion", "error");
            return Promise.reject({
                code: 400,
                message: 'No file name provided for deletion',
                status: 400
            });
        }
        return new Promise((resolve, reject) => {
            db.deleteImage(fileName).then((result) => {
                if (result) {
                    Logger.info("Image deleted successfully");
                    this.dropzoneDiv.classList.remove('mscp-dropzone-success');
                    resolve(true);
                } else {
                    Logger.error("Error deleting image");
                    reject(false);
                }
            }).catch((error) => {
                Logger.error('Error deleting image:', error);
                reject(error);
            });
        });

    }

    async reload() {
        Logger.debug('Upload.reload() called, removing dropzone');
        this.dropzoneDiv?.remove();
        this.dropzoneDiv = null; // Reset reference so createDropzone can create a new one
        await this.createDropzone();
    }

    // showMessage(msg, type = 'info') {
    //     this.cleanMessages();

    //     const message = document.createElement('div');
    //     message.classList.add('mscp-dropzone-message-text');
    //     if (type === 'error') message.classList.add('error');
    //     message.innerHTML = msg;
    //     this.dropzoneDiv?.appendChild(message);
    // }

    // cleanMessages() {
    //     if (this.wrapperDiv) {
    //         const messages = this.wrapperDiv.querySelectorAll('.mscp-dropzone-message-text');
    //         messages.forEach(message => {
    //             message.remove();
    //         });
    //     }
    // }

}

export default Upload;
